import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { BrandingInterface } from "../../common/interfaces/BrandingInterface";
import {
    loadBrandingFiles,
    loadBrandingThumbnails,
} from "../../common/utils/imageLoader";

export enum BrandingTypeEnum {
    VIDEOS = "branding_video",
    PRESENTATIONS = "branding_presentation",
}

export const setBrandingItems = createAsyncThunk(
    "branding/setBrandingItems",
    async (payload: {
        brandingItems: BrandingInterface[];
        brandingType: BrandingTypeEnum;
        getLocalBrandingFileUrl: (url: string) => Promise<string | null>;
    }) => {
        const { brandingType, getLocalBrandingFileUrl } = payload;

        const brandingItems = payload.brandingItems.filter(
            (item) => item.type === brandingType,
        );
        const brandingImages = await loadBrandingFiles(
            brandingItems,
            getLocalBrandingFileUrl,
        );

        const brandingThumbnails = await loadBrandingThumbnails(
            brandingItems,
            getLocalBrandingFileUrl,
        );

        return {
            brandingItems,
            brandingImages,
            brandingThumbnails,
        };
    },
);

interface BrandingPageInterface {
    brandingType: BrandingTypeEnum;
    brandingItems: BrandingInterface[];
    brandingImages: { [key: string]: string };
    brandingThumbnails: { [key: string]: string };
    isProcessingBrandingItems: boolean;
}

const initialState: BrandingPageInterface = {
    brandingType: BrandingTypeEnum.VIDEOS,
    brandingItems: [],
    brandingImages: {},
    brandingThumbnails: {},
    isProcessingBrandingItems: false,
};

const brandingSlice = createSlice({
    name: "branding",
    initialState,
    reducers: {
        setBrandingType: (state, action: PayloadAction<BrandingTypeEnum>) => {
            state.brandingType = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setBrandingItems.pending, (state) => {
                state.isProcessingBrandingItems = true;
            })
            .addCase(setBrandingItems.fulfilled, (state, action) => {
                state.brandingItems = action.payload.brandingItems;
                state.brandingImages = action.payload.brandingImages;
                state.brandingThumbnails = action.payload.brandingThumbnails;
                state.isProcessingBrandingItems = false;
            })
            .addCase(setBrandingItems.rejected, (state) => {
                state.isProcessingBrandingItems = false;
            });
    },
});

export const brandingReducer = brandingSlice.reducer;

export const { setBrandingType } = brandingSlice.actions;
